import type { WbgtStationTimeline } from '@heat-pwa/types';

export type Class10Feature = GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> & {
  id: number;
  properties: { code: string; name: string; enName?: string };
};

export type Class10FeatureCollection = GeoJSON.FeatureCollection & {
  features: Class10Feature[];
};

/**
 * Load the JMA "二次細分区域" boundary GeoJSON that ships with the PWA
 * as /class10.geojson, normalising the features so each has a unique
 * top-level `id` (needed for maplibre's `setFeatureState`).
 */
export async function fetchClass10(): Promise<Class10FeatureCollection> {
  const res = await fetch('/class10.geojson', { cache: 'force-cache' });
  if (!res.ok) throw new Error(`class10 fetch failed: ${res.status}`);
  const raw = (await res.json()) as GeoJSON.FeatureCollection;
  return {
    type: 'FeatureCollection',
    features: raw.features.map(
      (f, i) =>
        ({
          ...f,
          id: i,
        }) as Class10Feature,
    ),
  };
}

/**
 * Bounding-box centre per polygon, keyed by feature id. Good enough for
 * "which station is nearest" lookups — true centroids would require a
 * heavier geometry library for the cost of a few km of accuracy.
 */
export function computeCentroids(fc: Class10FeatureCollection): Map<number, [number, number]> {
  const out = new Map<number, [number, number]>();
  for (const f of fc.features) {
    out.set(f.id, bboxCenter(f.geometry));
  }
  return out;
}

/**
 * For every polygon, the WBGT station id closest (great-circle distance
 * approximated as Euclidean on lng/lat — fine at Japan's scale) to its
 * centroid.
 */
export function computeNearestStations(
  centroids: Map<number, [number, number]>,
  stations: readonly WbgtStationTimeline[],
): Map<number, string> {
  const out = new Map<number, string>();
  for (const [id, [cx, cy]] of centroids) {
    let bestCode = '';
    let bestDist = Number.POSITIVE_INFINITY;
    for (const s of stations) {
      const dx = s.lng - cx;
      const dy = s.lat - cy;
      const d = dx * dx + dy * dy;
      if (d < bestDist) {
        bestCode = s.stationCode;
        bestDist = d;
      }
    }
    if (bestCode) out.set(id, bestCode);
  }
  return out;
}

function bboxCenter(geom: GeoJSON.Geometry): [number, number] {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  const visit = (coords: unknown): void => {
    if (Array.isArray(coords) && typeof coords[0] === 'number') {
      const x = coords[0] as number;
      const y = coords[1] as number;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    } else if (Array.isArray(coords)) {
      for (const c of coords) visit(c);
    }
  };
  if ('coordinates' in geom) visit(geom.coordinates);
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}
